import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { api } from '../../services/api';
import { useToast } from "../../components/ToastContext";

const AddNewClient = ({ open, onClose, clientData, onSuccess }) => {
  const theme = useTheme();
  const toast = useToast();

  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [timeZones, setTimeZones] = useState([]);
  
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedTimeZone, setSelectedTimeZone] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  
  // 1. Replaced single string with an object to track all field errors
  const [errors, setErrors] = useState({});

useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [clientsRes, locationsRes] = await Promise.all([
          api.get('/Client/GetAllClientDetails'),
          api.get('/Client/location')
        ]);

        const rawData = clientsRes.data;
        const clientList = Array.isArray(rawData) ? rawData : (rawData?.data || []);

        const activeClients = clientList.filter((client) => {
          const activeState = client.isActive ?? client.IsActive ?? client.is_active;
          
          return activeState === true || activeState === "true" || activeState === 1;
        });

        setClients(activeClients);

        const locationList = Array.isArray(locationsRes.data) 
          ? locationsRes.data 
          : (locationsRes.data?.data || []);
        setLocations(locationList);

      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };

    if (open) {
      fetchDropdowns();
    }
  }, [open]);

  useEffect(() => {
    if (open && clientData) {
      setOrganizationName(clientData.organizationName || '');
      setSelectedClient(clientData.clientId || '');
      setSelectedLocation(clientData.locationId || '');
      setSelectedTimeZone(clientData.timeZoneID || '');
      
      setErrors({});

      if (clientData.locationId) {
        api.get(`/Client/timezone/${clientData.locationId}`)
          .then(res => setTimeZones(res.data || []))
          .catch(err => console.error(err));
      }
    } else if (open && !clientData) {
      setOrganizationName('');
      setSelectedClient('');
      setSelectedLocation('');
      setSelectedTimeZone('');
      setTimeZones([]);
      
      setErrors({});
    }
  }, [open, clientData]);

  const handleLocationChange = async (event) => {
    const locationId = event.target.value;
    setSelectedLocation(locationId);
    setSelectedTimeZone('');
    setErrors(prev => ({ ...prev, location: '' })); // Clear location error

    try {
      const response = await api.get(`/Client/timezone/${locationId}`);
      setTimeZones(response.data || []);
    } catch (error) {
      console.error('Error fetching time zones:', error);
    }
  };

  const handleSave = async () => {
    const userId = sessionStorage.getItem('userId');
    
    setErrors({});

    // 2. Comprehensive validation check for all fields
    let newErrors = {};
    if (!selectedClient) newErrors.client = 'Client is required';
    if (!organizationName) newErrors.organizationName = 'Organization Name is required';
    if (!selectedLocation) newErrors.location = 'Location is required';
    if (!selectedTimeZone) newErrors.timeZone = 'TimeZone is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (clientData?.organizationId) {
        const updatePayload = {
          organizationName: String(organizationName),
          organizationLocation: Number.parseInt(selectedLocation, 10),
          timeZone: String(selectedTimeZone),
          requesterId: Number.parseInt(userId, 10),
          isActive: clientData.isActive,
        };

        const response = await api.post(`/Client/UpdateOrganizationDetails/${clientData.organizationId}`, updatePayload);
        if (response.status === 200) {
          toast.success('Organization updated successfully');
          onSuccess();
        }
      } else {
        const createPayload = {
          OrganizationName: organizationName,
          OrganizationLocation: selectedLocation,
          TimeZone: selectedTimeZone,
          RequesterId: userId,
          ClientId: selectedClient,
        };

        const response = await api.post('/Client/CreateOrganizationDetails', createPayload);
        if (response.status === 200 || response.status === 201) {
          toast.success('Organization created successfully');
          onSuccess(); 
        }
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;

      if (apiErrorMessage) {
        toast.error(apiErrorMessage);
        // Bind the API conflict error to the Organization Name field
        setErrors({ organizationName: apiErrorMessage });
      } else {
        toast.error('An error occurred while saving the organization.');
      }
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
        {clientData?.organizationId ? 'Edit Organization' : 'Add New Organization'}
      </DialogTitle>
      
      <DialogContent 
        sx={{ 
          bgcolor: theme.palette.background.paper, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2.5, 
          pt: 2 
        }}
      >
        <TextField
          select
          fullWidth
          label="Client *"
          value={selectedClient}
          onChange={(e) => {
            setSelectedClient(e.target.value);
            setErrors(prev => ({ ...prev, client: '' }));
          }}
          disabled={!!clientData?.organizationId}
          sx={{mt:2}}
          error={!!errors.client}
          helperText={errors.client}
        >
          {clients.map((client) => (
            <MenuItem key={client.client_Id || client.clientId} value={client.client_Id || client.clientId}>
              {client.client_Name || client.clientName}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Organization Name *"
          fullWidth
          value={organizationName}
          onChange={(e) => {
            setOrganizationName(e.target.value);
            setErrors(prev => ({ ...prev, organizationName: '' }));
          }}
          error={!!errors.organizationName}
          helperText={errors.organizationName}
        />

        <TextField
          select
          fullWidth
          label="Location *"
          value={selectedLocation}
          onChange={handleLocationChange}
          error={!!errors.location}
          helperText={errors.location}
        >
          {locations.map((location, index) => (
            <MenuItem key={index} value={location.value}>
              {location.text}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="TimeZone *"
          value={selectedTimeZone}
          onChange={(e) => {
            setSelectedTimeZone(e.target.value);
            setErrors(prev => ({ ...prev, timeZone: '' }));
          }}
          disabled={!selectedLocation}
          error={!!errors.timeZone}
          helperText={errors.timeZone}
        >
          {timeZones.map((timeZone, index) => (
            <MenuItem key={index} value={timeZone.value}>
              {timeZone.text}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>

      <DialogActions sx={{ py: 1.5, px:3, bgcolor: theme.palette.background.default }}>
        <Button onClick={handleClose} variant="outlined" 
          sx={{ 
            p: 0.7,
            px: 2,
            color: theme.palette.mode === 'dark' ? 'grey.300' : 'text.secondary',
            borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.400',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
              borderColor: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.600',
            }
          }}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" sx={{py:1.2}}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewClient;